import {
  Alert,
  Button,
  Field,
  Label,
  Select,
  Skeleton,
  Spinner,
  ValidationMessage,
} from "@digdir/designsystemet-react";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/api/endpoints";
import { formatOrgNr } from "@/utils";
import { useAuth } from "../auth";
import { useSelskaper } from "./SelskapContext";

type SelskapFormInputs = {
  selectedOrganisasjonsnummer: number; // Organisasjonsnummer
};

export function SelskapForm() {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    selskaper,
    isLoadingSelskaper,
    isErrorLoadingSelskaper,
    active,
    setActiveSelskap,
  } = useSelskaper();

  const authContext = useAuth();

  const [isAuthenticatingWithOr, setIsAuthenticatingWithOrg] = useState(false);
  const [authenticationWithOrgFailed, setAuthenticationWithOrgFailed] =
    useState<number | null>(null);

  const defaultValue =
    active?.organisasjonsnummer != null
      ? { selectedOrganisasjonsnummer: active.organisasjonsnummer }
      : undefined;

  const form = useForm<SelskapFormInputs>({
    values: defaultValue,
  });

  useEffect(() => {
    const callback = form.subscribe({
      formState: {
        values: true,
      },
      callback: () => {
        // Reset authentication error when changing selection
        setAuthenticationWithOrgFailed(null);
      },
    });

    return () => callback();
  }, [form.subscribe]);

  const onSubmit: SubmitHandler<SelskapFormInputs> = async (data) => {
    const selectedOrgNr = data.selectedOrganisasjonsnummer;
    const selectedAktoer = selskaper.find(
      (a) => a.organisasjonsnummer === selectedOrgNr,
    );

    if (selectedAktoer == null) {
      throw new Error("No selskap found for selected organization number");
    }

    setAuthenticationWithOrgFailed(null);
    if (!(await tryAuthenticateWithOrgNr(selectedOrgNr))) {
      setAuthenticationWithOrgFailed(selectedOrgNr);
      return;
    }

    setActiveSelskap(selectedAktoer);
    router.navigate({ to: "/bestillinger" });
  };

  const sortedSelskaper = selskaper.slice().sort((a, b) => {
    return a.navn.localeCompare(b.navn);
  });

  const tryAuthenticateWithOrgNr = async (orgnr: number): Promise<boolean> => {
    try {
      setIsAuthenticatingWithOrg(true);

      if (!authContext.isAuthenticated) {
        router.navigate({ to: "/auth/logout" });
      }

      await apiClient.auth.signinWithOrg(orgnr);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setIsAuthenticatingWithOrg(false);
    }
  };

  const selectId = useId();
  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Field>
        <Label htmlFor={selectId} className="font-semibold">
          {t("pages.velg-selskap.form.label")}
        </Label>
        {isLoadingSelskaper ? (
          <Skeleton className="h-10 w-full mt-4" />
        ) : isErrorLoadingSelskaper ? (
          <Alert data-color={"danger"}>
            {t("pages.velg-selskap.form.feil.laste-feil")}
          </Alert>
        ) : sortedSelskaper.length < 1 ? (
          <div>{t("pages.velg-selskap.form.feil.ingen-tilganger")}</div>
        ) : (
          <>
            <Select
              id={selectId}
              {...form.register("selectedOrganisasjonsnummer", {
                required: true,
                valueAsNumber: true,
              })}
            >
              <Select.Option value={undefined}></Select.Option>
              {sortedSelskaper?.map((selskap) => (
                <Select.Option
                  key={selskap.organisasjonsnummer}
                  value={selskap.organisasjonsnummer}
                >
                  {selskap.navn} ({formatOrgNr(selskap.organisasjonsnummer)})
                </Select.Option>
              ))}
            </Select>
            {form.formState.errors.selectedOrganisasjonsnummer && (
              <ValidationMessage role="alert" className="mt-1">
                {t("pages.velg-selskap.form.feil.pakrevd")}
              </ValidationMessage>
            )}
            {isAuthenticatingWithOr ? (
              <AutentisererSelskap />
            ) : (
              <>
                {authenticationWithOrgFailed != null && (
                  <AutentiserSelskapMisslyktes
                    orgnr={authenticationWithOrgFailed}
                  />
                )}
                <Button className="mt-6" type="submit">
                  {t("pages.velg-selskap.form.velg")}
                </Button>
              </>
            )}
          </>
        )}
      </Field>
    </form>
  );
}

const AutentisererSelskap = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 mt-4">
      <Spinner
        aria-label={t("pages.velg-selskap.form.autentiserer-aria-label")}
      />
      <span>{t("pages.velg-selskap.form.autentiserer")}</span>
    </div>
  );
};

const AutentiserSelskapMisslyktes = ({ orgnr }: { orgnr: number }) => {
  const { t } = useTranslation();
  return (
    <Alert data-color={"danger"} className="mt-4">
      <span>
        {t("pages.velg-selskap.form.autentiser-mislyktes", { orgnr: orgnr })}
      </span>
    </Alert>
  );
};
